import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/apiClient';
import { Channel, ChannelVisibility } from '../types/channel';

export const MAX_CHANNELS_PER_USER = 5;

interface UseChannelResult {
  channels: Channel[];
  channel: Channel | null;
  activeChannel: Channel | null;
  activeChannelId: string | null;
  setActiveChannelId: (id: string | null) => void;
  maxChannels: number;
  canCreateMore: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  createChannel: (payload: {
    displayName: string;
    description?: string;
    tags?: string[];
    subject?: string;
    level?: string;
    language?: string;
    category?: string;
  }) => Promise<Channel | null>;
  updateChannel: (updates: Partial<Channel>) => Promise<Channel | null>;
  setVisibility: (visibility: ChannelVisibility) => Promise<Channel | null>;
  deleteChannel: () => Promise<boolean>;
}

const ChannelContext = createContext<UseChannelResult | null>(null);

export const ChannelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [maxChannels, setMaxChannels] = useState<number>(MAX_CHANNELS_PER_USER);
  // null means "create new channel" mode — intentionally separate from undefined
  const [activeChannelId, setActiveChannelIdRaw] = useState<string | null>(null);
  // Tracks whether the user explicitly requested create mode so fetch doesn't override it
  const [createModeIntent, setCreateModeIntent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMyChannels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getMyChannels();
      if (result.data) {
        const fetched = result.data.channels;
        setChannels(fetched);
        setMaxChannels(result.data.maxChannels ?? MAX_CHANNELS_PER_USER);
        setActiveChannelIdRaw((prev) => {
          // Respect explicit create-mode intent (null set by user action)
          if (createModeIntent) return null;
          // Keep current selection if still valid
          if (prev && fetched.some((c) => c.id === prev)) return prev;
          // Default to first channel
          return fetched[0]?.id ?? null;
        });
      } else if (result.error) {
        setError(result.error.message);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [createModeIntent]);

  useEffect(() => {
    fetchMyChannels();
  }, [fetchMyChannels]);

  const activeChannel = channels.find((c) => c.id === activeChannelId) ?? null;
  const canCreateMore = channels.length < maxChannels;

  const setActiveChannelId = useCallback((id: string | null) => {
    setCreateModeIntent(id === null);
    setActiveChannelIdRaw(id);
  }, []);

  const createChannel = useCallback(async (payload: {
    displayName: string;
    description?: string;
    tags?: string[];
    subject?: string;
    level?: string;
    language?: string;
    category?: string;
  }): Promise<Channel | null> => {
    setError(null);
    const result = await apiClient.createChannel({ ...payload, type: 'personal' });
    if (result.data) {
      setChannels((prev) => [...prev, result.data!]);
      setCreateModeIntent(false);
      setActiveChannelIdRaw(result.data.id);
      return result.data;
    }
    setError(result.error?.message ?? 'Error creating channel');
    return null;
  }, []);

  const updateChannel = useCallback(async (updates: Partial<Channel>): Promise<Channel | null> => {
    if (!activeChannel) return null;
    setError(null);
    const result = await apiClient.updateChannel(activeChannel.id, updates);
    if (result.data) {
      setChannels((prev) => prev.map((c) => (c.id === result.data!.id ? result.data! : c)));
      return result.data;
    }
    setError(result.error?.message ?? 'Error updating channel');
    return null;
  }, [activeChannel]);

  const setVisibility = useCallback(async (visibility: ChannelVisibility): Promise<Channel | null> => {
    if (!activeChannel) return null;
    setError(null);
    const result = await apiClient.patchChannelVisibility(activeChannel.id, visibility);
    if (result.data) {
      setChannels((prev) => prev.map((c) => (c.id === result.data!.id ? result.data! : c)));
      return result.data;
    }
    setError(result.error?.message ?? 'Error updating visibility');
    return null;
  }, [activeChannel]);

  const deleteChannel = useCallback(async (): Promise<boolean> => {
    if (!activeChannel) return false;
    setError(null);
    const result = await apiClient.deleteChannel(activeChannel.id);
    if (!result.error) {
      const remaining = channels.filter((c) => c.id !== activeChannel.id);
      setChannels(remaining);
      setCreateModeIntent(false);
      setActiveChannelIdRaw(remaining[0]?.id ?? null);
      return true;
    }
    setError(result.error.message);
    return false;
  }, [activeChannel, channels]);

  return (
    <ChannelContext.Provider value={{
      channels,
      channel: activeChannel,
      activeChannel,
      activeChannelId,
      setActiveChannelId,
      maxChannels,
      canCreateMore,
      loading,
      error,
      refresh: fetchMyChannels,
      createChannel,
      updateChannel,
      setVisibility,
      deleteChannel,
    }}>
      {children}
    </ChannelContext.Provider>
  );
};

export const useChannel = (): UseChannelResult => {
  const ctx = useContext(ChannelContext);
  if (!ctx) throw new Error('useChannel must be used within <ChannelProvider>');
  return ctx;
};
